import { useEffect, useState } from 'react';

import { getAuthProviders, type AuthProviders } from 'src/pages/admin/services/authService';

// ----------------------------------------------------------------------

// Until the service answers, and if it fails, the console offers Google only: showing the password
// form first would flash a form the service refuses when password login is off (XBIT-4719).
const FALLBACK: AuthProviders = { password: false, google: true };

// One request per page load, shared by the sign-in page and the authenticated layout.
let providersPromise: Promise<AuthProviders> | null = null;
let resolved: AuthProviders | null = null;

function loadProviders(): Promise<AuthProviders> {
  if (!providersPromise) {
    providersPromise = getAuthProviders()
      .catch(() => FALLBACK)
      .then((providers) => {
        resolved = providers;
        return providers;
      });
  }
  return providersPromise;
}

/**
 * Whether the username/password form is worth showing. A service that does not report `twoFactor`
 * is a legacy one (today's behaviour). One that reports it without `totpRequired` is XBIT-4742's:
 * at least one usable method. One that reports `totpRequired` (XBIT-4796) always runs the emailed
 * code first, so email OTP must be usable, and TOTP too when it is required - else it refuses every
 * password sign-in.
 */
export function isPasswordFormAvailable(providers: AuthProviders): boolean {
  const { twoFactor } = providers;
  if (!providers.password) return false;
  if (!twoFactor) return true;
  if (twoFactor.totpRequired === undefined) return twoFactor.totp || twoFactor.emailOtp;
  return twoFactor.emailOtp && (!twoFactor.totpRequired || twoFactor.totp);
}

/** "Sign in with Google" is worth showing unless TOTP is required and cannot be met (XBIT-4796 D3, D13). */
export function isGoogleAvailable(providers: AuthProviders): boolean {
  return providers.google && !(providers.twoFactor?.totpRequired && !providers.twoFactor.totp);
}

export function useAuthProviders(): AuthProviders {
  const [providers, setProviders] = useState<AuthProviders>(resolved ?? FALLBACK);

  useEffect(() => {
    let active = true;
    loadProviders().then((value) => {
      if (active) setProviders(value);
    });
    return () => {
      active = false;
    };
  }, []);

  return providers;
}
